<template>
  <form class="customer-form" @submit.prevent="onSubmit">
    <div class="d-flex align-center thor-profile-avatar-name-gap">
      <v-avatar color="primary" size="48" rounded="lg" variant="flat" class="shrink-0">
        <span class="text-h6 font-weight-bold text-white">{{ nameInitial }}</span>
      </v-avatar>
      <v-text-field
        v-model="state.name"
        label="Customer name"
        variant="outlined"
        density="comfortable"
        hide-details="auto"
        required
        autocomplete="organization"
        :disabled="state.loading || state.disabled"
        class="customer-form__input flex-grow-1 min-w-0 mb-0"
      />
    </div>
    <div class="customer-form__actions d-flex justify-end flex-wrap gap-2 mt-4 pt-4">
      <v-btn
        variant="text"
        :disabled="state.loading"
        class="text-transform-none"
        type="button"
        @click="emit('cancel')"
      >
        Cancel
      </v-btn>
      <v-btn
        v-if="props.type === 'edit'"
        color="primary"
        :loading="state.loading"
        :disabled="state.disabled"
        class="text-transform-none"
        type="submit"
      >
        Update
      </v-btn>
      <v-btn
        v-if="props.type === 'create'"
        color="primary"
        :loading="state.loading"
        :disabled="state.disabled"
        class="text-transform-none"
        type="submit"
      >
        Create
      </v-btn>
    </div>
  </form>
</template>

<script setup lang="ts">
import { URLs } from "@/constants";
import { useAuthStore } from "@/stores/auth";
import { useCustomerStore } from "@/stores/customer";
import type { IApp } from "@/types";
import { makeAlert } from "@/utils";
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

const props = defineProps<
  | {
      type: "edit";
      customer: IApp.ICustomer | null;
    }
  | {
      type: "create";
      customer: IApp.ICreateData<IApp.ICustomer> | null;
    }
>();

const emit = defineEmits<{
  cancel: [];
}>();

const state = ref<{
  name: string;
  loading: boolean;
  disabled: boolean;
}>({
  name: props?.customer?.name ?? "",
  loading: false,
  disabled: false
});

const nameInitial = computed(() => {
  const n = state.value.name?.trim();
  if (!n) return "?";
  return n.charAt(0).toUpperCase();
});

watch(
  () => props.customer?.name,
  (name) => {
    if (name !== undefined) state.value.name = name;
  }
);

const customerStore = useCustomerStore();
const authStore = useAuthStore();
const user = computed(() => authStore.user);
const router = useRouter();

function onSubmit() {
  if (props.type === "create") {
    void handleClickCreate();
  } else {
    void handleClickPatch();
  }
}

const handleClickCreate = async () => {
  state.value.loading = true;
  state.value.disabled = true;
  const response = await customerStore.createCustomer(user.value?.id!, {
    name: state.value.name
  });
  if (response.status) {
    makeAlert("Customer created successfully", "success");
    await customerStore.fetchCustomers(user?.value?.id!);
    setTimeout(() => {
      router.push(URLs.PAGE_CUSTOMER_SOURCES(response.data.customer.id));
    }, 1000);
  } else {
    makeAlert(response.message, "error");
    state.value.disabled = false;
  }
  state.value.loading = false;
};

const handleClickPatch = async () => {
  state.value.loading = true;
  state.value.disabled = true;
  const response = await customerStore.patchCustomer(user.value?.id!, {
    id: props?.customer?.id ?? "",
    name: state.value.name
  });
  if (response.status) {
    await customerStore.fetchCustomers(user.value?.id!);
    makeAlert("Customer updated successfully", "success");
  } else {
    makeAlert(response.message, "error");
  }
  state.value.disabled = false;
  state.value.loading = false;
};
</script>

<style scoped>
.customer-form__actions {
  border-top: thin solid rgba(0, 0, 0, 0.08);
}
</style>
